import { useLiveQuery } from 'dexie-react-hooks'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { APP_VERSION, useClub, useEngineState } from '../club/ClubProvider.tsx'
import { formatLocal } from '../lib/time.ts'
import { Banner, errorText, StatusPill } from '../ui/components.tsx'
import { Picto } from '../ui/pictos.tsx'
import { usePhotosWaiting } from '../photos/usePhotos.ts'
import { localOnlyTables, type OutboxRow } from '../sync/db.ts'
import { settleTransient } from '../sync/transient.ts'
import { attentionRows, recentRows, waitingRows } from '../sync/lists.ts'

/** What a refused or conflicting command was about, in words, and where the person can redo it by hand (a refused fact is never re-sent as it was: the screen it came from takes the corrected one). */
function commandTitle(t: (k: string, o?: Record<string, unknown>) => string, r: OutboxRow): string {
  return t(`sync.command.${r.name.replace('.', '_')}`, { defaultValue: r.name })
}
function redoLink(r: OutboxRow): { to: string; params?: Record<string, string> } | null {
  const p = r.payload as Record<string, string | undefined>
  switch (r.name) {
    case 'job.start': case 'job.pause': case 'job.resume': case 'job.finish': return p.job_id ? { to: '/work/start' } : null
    case 'work.manual_entry': case 'work.correct': return { to: '/work/manual' }
    case 'inspection.submit': return p.asset_id ? { to: '/assets/$assetId/check', params: { assetId: p.asset_id } } : null
    case 'defect.raise': return p.asset_id ? { to: '/assets/$assetId/defect', params: { assetId: p.asset_id } } : null
    case 'meter.record': return { to: '/scan' }
    default: return null
  }
}

export function SyncPage() {
  const { t } = useTranslation()
  const { db, engine, site, deviceId, platform } = useClub()
  const st = useEngineState()
  // each list by its own statuses over the whole outbox, as the header badge counts them (sync/lists.ts)
  const waiting = useLiveQuery(() => waitingRows(db), [db], [])
  const attention = useLiveQuery(() => attentionRows(db), [db], [])
  const recent = useLiveQuery(() => recentRows(db), [db], [])
  const photos = usePhotosWaiting()
  const green = waiting.length === 0 && attention.length === 0 && photos.waiting === 0 && photos.failed === 0 && st.online === true

  async function discardPhoto(sha: string) { await db.blobs.delete(sha) }
  async function discard(id: string) {
    await db.transaction('rw', [db.outbox, db.overlay, ...localOnlyTables(db)], async () => { await db.outbox.update(id, { status: 'discarded' }); await db.overlay.where('command_id').equals(id).delete(); await settleTransient(db, id) })
  }
  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">{t('sync.title')}</h1>

      <div className={`card flex flex-col gap-2 ${green ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-950' : 'border-amber-500 bg-amber-50 dark:bg-amber-950'}`} data-testid="end-of-shift">
        <h2 className="text-lg font-bold">{t('sync.endOfShift')}</h2>
        <p>{green ? t('sync.endOfShiftOk') : t('sync.endOfShiftPending')}</p>
        <button className="btn btn-primary" onClick={() => void engine.sync({ force: true })} disabled={st.phase === 'syncing'}>{st.phase === 'syncing' ? t('sync.syncing') : t('sync.now')}</button>
        <p className="meta">{st.lastSyncAt ? t('sync.lastSync', { when: formatLocal(st.lastSyncAt, site.timezone) }) : t('sync.lastSync', { when: t('sync.never') })}</p>
        {/* M23 item 8: with no network the page says so in French (the engine's raw "Failed to fetch" is only under the technical details below) */}
        {st.online === false && <p className="meta" data-testid="sync-offline-line">{t('sync.offlineLine')}</p>}
      </div>

      {st.authRequired && <Banner tone="warn">{t('auth.signIn')}</Banner>}
      {st.frozen && <Banner tone="warn">{t('sync.offline')}</Banner>}

      {attention.length > 0 && (
        <section aria-label={t('sync.needsAttention')} className="flex flex-col gap-2">
          <h2 className="text-lg font-bold text-red-800">{t('sync.needsAttention')}</h2>
          {attention.map((r) => (
            <div key={r.command_id} className="card border-red-400">
              <div className="flex items-center justify-between gap-2"><span className="font-semibold" data-testid="attention-command">{commandTitle(t, r)}</span><StatusPill status={r.status} /></div>
              <p className="text-sm" data-testid="attention-why">{errorText(t, r.server_code)}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {(() => { const l = redoLink(r); return l ? <Link to={l.to as never} params={l.params as never} className="btn" data-testid="attention-redo">{t('sync.editResend')}</Link> : null })()}
                <button className="btn" onClick={() => void discard(r.command_id)}>{t('sync.dismiss')}</button>
              </div>
            </div>
          ))}
        </section>
      )}

      {(photos.waiting > 0 || photos.failed > 0) && (
        <section aria-label={t('sync.photos')} className="flex flex-col gap-2" data-testid="sync-photos">
          <h2 className="text-lg font-bold">{t('sync.photos')}</h2>
          {photos.waiting > 0 && <p data-testid="sync-photos-waiting">{t('photos.waitingCount', { count: photos.waiting })}</p>}
          {photos.rows.filter((r) => r.state === 'failed').map((r) => (
            <div key={r.sha256} className="card flex items-center justify-between gap-2 border-red-400">
              <span className="text-sm">{t('photos.failed')}{r.last_error ? ` · ${errorText(t, r.last_error)}` : ''}</span>
              <button className="btn" onClick={() => void discardPhoto(r.sha256)}>{t('sync.dismiss')}</button>
            </div>
          ))}
        </section>
      )}

      <section aria-label={t('sync.outbox')} className="flex flex-col gap-2">
        <h2 className="text-lg font-bold">{t('sync.outbox')}</h2>
        {waiting.length === 0 && <p className="opacity-70">{t('sync.empty')}</p>}
        {waiting.map((r) => (
          <div key={r.command_id} className="card flex items-center justify-between gap-2" data-testid="outbox-row">
            <span className="min-w-0 truncate"><span className="font-semibold">{r.name}</span> <span className="text-sm opacity-70">{(r.payload as { readable_tag?: string }).readable_tag ?? ''}</span></span>
            <StatusPill status={r.status} />
          </div>
        ))}
        {recent.map((r) => (
          <div key={r.command_id} className="flex items-center justify-between gap-2 px-1 text-sm opacity-80">
            <span className="flex min-w-0 flex-col"><span className="truncate">{commandTitle(t, r)} {(r.payload as { readable_tag?: string }).readable_tag ?? ''}</span>
              {(r.exceptions ?? []).map((c) => <span key={c} className="text-amber-800 dark:text-amber-300" data-testid="recent-exception">{errorText(t, c)}</span>)}</span><StatusPill status={r.status} />
          </div>
        ))}
      </section>

      {/* M23 items 5 and 61: the device's technical words ("mac · chromium · browser · field", the UUID) behind a fold, said in the person's language; the build's version stays (it is how a report names a build) */}
      <details className="group card text-sm" data-testid="sync-tech">
        <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-3 font-bold [&::-webkit-details-marker]:hidden" data-testid="sync-tech-toggle">
          <span className="flex items-center gap-2"><Picto name="settings" className="size-5 text-brand dark:text-fairway" />{t('sync.techDetails')}</span>
          <Picto name="chevron" className="size-5 shrink-0 rotate-90 transition-transform group-open:-rotate-90" strokeWidth={2.2} />
        </summary>
        <dl className="mt-2 grid grid-cols-1 gap-y-0.5 [&>dd]:mb-2 sm:grid-cols-[auto_1fr] sm:gap-x-4 sm:gap-y-1 sm:[&>dd]:mb-0">
          <dt className="font-semibold">{t('sync.deviceId')}</dt><dd className="break-all" data-testid="device-id">{deviceId}</dd>
          <dt className="font-semibold">{t('sync.system')}</dt><dd>{t(`sync.os.${platform.os}`)} · {t(`sync.browser.${platform.browser}`)}</dd>
          <dt className="font-semibold">{t('sync.modeLabel')}</dt><dd>{t(platform.standalone ? 'sync.installed' : 'sync.inBrowser')} · {t(`sync.mode.${platform.mode}`)}</dd>
          <dt className="font-semibold">{t('sync.versionLabel')}</dt><dd data-testid="app-version">{APP_VERSION}</dd>
          {st.lastError && <><dt className="font-semibold">{t('sync.lastError')}</dt><dd className="break-all" data-testid="sync-last-error">{st.lastError}</dd></>}
        </dl>
      </details>
    </section>
  )
}
